import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const ARROW_SIZE = 15;

const arrowPosition = (position) => {
  switch (position) {
    case 'top':
      return `
        top: -15px;
        bottom: auto;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        transform: rotate(0deg);
      `;
    case 'bottom':
      return `
        bottom: -15px;
        top: auto;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        transform: rotate(180deg);
      `;
    case 'left':
      return `
        top: 0;
        bottom: 0;
        margin-top: auto;
        margin-bottom: auto;
        left: -22px;
        transform: rotate(-90deg);
      `;
    case 'right':
    default:
      return `
        top: 0;
        bottom: 0;
        margin-top: auto;
        margin-bottom: auto;
        right: -22px;
        transform: rotate(90deg);
      `;
  }
};

const Arrow = styled.div`
    position: absolute;
    display: block;
    width: 30px;
    height: ${ARROW_SIZE}px;
    content: "";
    background-color: transparent;
    overflow: hidden;
    ${props => arrowPosition(props.arrow)}

    @media ${({ theme }) => theme.breakpoints.mobile} {
        ${props => (props.arrowMobile ? arrowPosition(props.arrowMobile) : '')}
    }

    &:after {
      top: 8px;
      left: 8px;
      position: absolute;
      display: block;
      box-sizing: border-box;
      width: ${ARROW_SIZE}px;
      height: ${ARROW_SIZE}px;
      content: "";
      border-radius: 2px;
      border-color: inherit;
      background-color: white;
      overflow: hidden;
      transform: rotate(45deg);
      box-shadow: 0 0 6px 0 rgba(33, 48, 56, 0.2);
    }
`;

const TooltipBox = styled.div.attrs(props => ({ 'data-qa': props.qa, 'tabIndex': 0 }))`
  ${({
    theme,
    compact,
    floating,
    noSpacing,
    rounded,
    small,
    textAlign
  }) => css`
    position: relative;
    padding: 24px;
    background-color: ${theme.colors.neutral.white};
    color: ${theme.colors.neutral.currysBlack};
    font-size: 13px;
    text-align: ${textAlign};
    font-family: ${theme.fonts.default.fontFamily};
    box-shadow: 0 0 6px 0 rgba(33, 48, 56, 0.2);

    @media ${theme.breakpoints.mobile} {
      padding: ${theme.spacing[3]};
    }

    ${rounded && css`
      border-radius: 7px;
    `};

    ${floating && css`
      position: absolute;
      left: 50%;
      z-index: 2;
      margin-top: 20px;
      margin-left: -50%;
      width: 100%;
    `};

    ${small && css`
      margin-top: 15px;
      padding: ${theme.spacing[3]} ${theme.spacing[4]};
    `};

    ${compact && css`
      padding: 10px;
    `};

    ${noSpacing && css`
      margin: 0;
      padding: 0;
    `};
  `};
`;

/**
 * @tags styled, tooltip, alert
 */
const Tooltip = ({ arrow, arrowMobile, ...props }) => (
  <TooltipBox {...props}>
    {arrow && <Arrow arrow={arrow} arrowMobile={arrowMobile} />} {props.children}
  </TooltipBox>
);

Tooltip.defaultProps = {
  arrow: undefined,
  arrowMobile: undefined,
  children: undefined,
  compact: false,
  floating: false,
  noSpacing: false,
  rounded: true,
  shift: 0,
  shiftMobile: 0,
  small: false,
  textAlign: 'left',
  qa: undefined
};

Tooltip.displayName = 'Tooltip';

Tooltip.propTypes = {
  arrow: PropTypes
    .oneOf([
      'top',
      'right',
      'bottom',
      'left'
    ]),
  arrowMobile: PropTypes
    .oneOf([
      'top',
      'right',
      'bottom',
      'left'
    ]),
  children: PropTypes.node,
  compact: PropTypes.bool,
  qa: PropTypes.string,
  /** Used only in `Animated` variant */
  floating: PropTypes.bool,
  noSpacing: PropTypes.bool,
  rounded: PropTypes.bool,
  shift: PropTypes.number,
  shiftMobile: PropTypes.number,
  small: PropTypes.bool,
  textAlign: PropTypes
    .oneOf([
      'left',
      'right',
      'center'
    ])
};

/** @component */
export default Tooltip;
